const ALLOWED_TAGS = new Set(["B", "STRONG", "H2", "A", "P", "BR", "UL", "OL", "LI", "EM", "I", "U", "SPAN", "DIV"]);

const isBoldLikeStyle = (styleValue: string) => {
  const normalized = String(styleValue || "").toLowerCase();
  return /font-weight\s*:\s*(bold|[7-9]00)/.test(normalized);
};

const isHeadingLikeStyle = (styleValue: string) => {
  const normalized = String(styleValue || "").toLowerCase();
  return /font-size\s*:\s*(1\.[4-9]|2|x-large|xx-large|24px|26px|28px|30px|32px)/.test(normalized);
};

const toSafeHref = (rawHref: string): string | null => {
  const href = String(rawHref || "").trim();
  if (!href) return null;

  if (href.startsWith("#") || href.startsWith("/")) {
    return href;
  }

  try {
    const parsed = new URL(href, window.location.origin);
    if (["http:", "https:", "mailto:", "tel:"].includes(parsed.protocol)) {
      return parsed.href;
    }
  } catch {
    return null;
  }

  return null;
};

const sanitizeNode = (node: Node, doc: Document): Node | null => {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const source = node as HTMLElement;
  const tag = source.tagName.toUpperCase();
  const safeChildren = Array.from(source.childNodes)
    .map((child) => sanitizeNode(child, doc))
    .filter(Boolean) as Node[];

  if (!ALLOWED_TAGS.has(tag)) {
    const fragment = doc.createDocumentFragment();
    safeChildren.forEach((child) => fragment.appendChild(child));
    return fragment;
  }

  if (tag === "SPAN") {
    const style = source.getAttribute("style") || "";

    if (isHeadingLikeStyle(style)) {
      const heading = doc.createElement("h2");
      safeChildren.forEach((child) => heading.appendChild(child));
      return heading;
    }

    if (isBoldLikeStyle(style)) {
      const strong = doc.createElement("strong");
      safeChildren.forEach((child) => strong.appendChild(child));
      return strong;
    }
  }

  if (tag === "DIV") {
    const paragraph = doc.createElement("p");
    safeChildren.forEach((child) => paragraph.appendChild(child));
    return paragraph;
  }

  const target = doc.createElement(tag.toLowerCase());

  if (tag === "A") {
    const safeHref = toSafeHref(source.getAttribute("href") || "");
    if (safeHref) {
      target.setAttribute("href", safeHref);
      target.setAttribute("target", "_blank");
      target.setAttribute("rel", "noopener noreferrer");
    }
  }

  safeChildren.forEach((child) => target.appendChild(child));
  return target;
};

export const sanitizeRichTextHtml = (html: string): string => {
  const raw = String(html || "").trim();
  if (!raw) return "";

  if (typeof document === "undefined") {
    return raw;
  }

  const template = document.createElement("template");
  template.innerHTML = raw;

  const output = document.createElement("div");
  Array.from(template.content.childNodes).forEach((node) => {
    const safeNode = sanitizeNode(node, document);
    if (safeNode) {
      output.appendChild(safeNode);
    }
  });

  return output.innerHTML;
};
